import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { getRegistrationStatus } from "@/lib/api";
import { formatDateRange, formatPrice } from "@/lib/format";

const PaiementSucces = () => {
  const [searchParams] = useSearchParams();
  const rid = searchParams.get("rid");
  const [status, setStatus] = useState("verifying"); // verifying | confirmed | pending
  const [confirmation, setConfirmation] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!rid) {
      setStatus("pending");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await getRegistrationStatus(rid);
        if (cancelled) return;
        if (data.registration_status === "confirmed" && data.payment_status === "paid") {
          setConfirmation(data.confirmation);
          setStatus("confirmed");
          return;
        }
      } catch {
        // on continue de vérifier silencieusement
      }
      attempts.current += 1;
      if (!cancelled && attempts.current < 14) {
        setTimeout(poll, 2500);
      } else if (!cancelled) {
        setStatus("pending");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [rid]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-6 pt-20">
      <div className="max-w-xl w-full bg-noir-surface border border-noir-border p-8 md:p-12 text-center">
        {status === "verifying" && (
          <div data-testid="payment-verifying">
            <Loader2 size={48} className="animate-spin text-gold mx-auto" />
            <h1 className="mt-6 font-display uppercase text-4xl md:text-5xl tracking-wide">
              Paiement en cours de <span className="text-gold">vérification</span>
            </h1>
            <p className="mt-4 text-white/60 leading-relaxed">
              Votre paiement est en cours de vérification auprès de Stripe. Cette page sera
              automatiquement mise à jour dès confirmation — ne fermez pas l'onglet.
            </p>
          </div>
        )}

        {status === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 size={48} className="animate-spin text-gold mx-auto" />
            <h1 className="mt-6 font-display uppercase text-4xl md:text-5xl tracking-wide">
              Paiement en cours de <span className="text-gold">confirmation</span>
            </h1>
            <p className="mt-4 text-white/60 leading-relaxed">
              La confirmation de votre paiement est en cours de traitement par Stripe. Vous recevrez
              un email de confirmation dès que votre inscription sera validée — inutile de réessayer.
            </p>
          </div>
        )}

        {status === "confirmed" && confirmation && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 size={56} className="text-gold mx-auto" />
            <h1 className="mt-6 font-display uppercase text-4xl md:text-5xl tracking-wide">
              Inscription <span className="text-gold">confirmée</span>
            </h1>
            <p className="mt-3 text-white/65">Merci pour votre inscription au {confirmation.stage_name}.</p>

            <div className="mt-8 text-left border border-white/10 divide-y divide-white/5">
              {[
                ["Participant", `${confirmation.child_first_name} ${confirmation.child_last_name}`],
                ["Stage", confirmation.stage_name],
                ["Dates", formatDateRange(confirmation.start_date, confirmation.end_date)],
                ["Lieu", confirmation.location],
                ["Catégorie", confirmation.category],
                ["Référence", confirmation.registration_reference || "—"],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-4 px-5 py-3.5">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{label}</span>
                  <span className="text-white text-sm font-semibold text-right">{value}</span>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-4 px-5 py-3.5 bg-gold/5">
                <span className="text-[11px] uppercase tracking-[0.2em] text-gold">Montant payé</span>
                <span data-testid="confirmed-amount" className="font-display text-2xl text-gold">{formatPrice(confirmation.amount)}</span>
              </div>
            </div>

            <p className="mt-6 text-white/50 text-sm flex items-center justify-center gap-2">
              <Mail size={14} className="text-gold" />
              Un email de confirmation vous sera envoyé prochainement.
            </p>
            <Link
              to="/"
              data-testid="confirmed-home-link"
              className="mt-8 inline-block px-8 py-4 border-2 border-gold text-gold text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all"
            >
              Retour à l'accueil
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaiementSucces;
